import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectionList } from '@angular/material/list';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { translate } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { BroadcasterService } from 'ng-broadcaster';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { PaginationInterface } from '../../../interface/pagination.interface';
import { TableColumn } from '../../../interface/table-column.interface';
import { TableData } from '../../../interface/table-data.interface';
import { TableRecordAction } from '../../../interface/table-record-action.interface';
import { TableCellDataTypesInterface } from '../../../interface/tablecelldatatype.interface';
import { CommonUtilsService } from '../../../services/commonutils/common-utils.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { SaveUiPreferences } from '../../../store/actions/ui-preferences.actions';
import { getTaskManagementPreference$, getUiPreferences$ } from '../../../store/selectors/ui-preferences.selector';

@Component({
    selector: 'material-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false,
})
export class MaterialTableComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('selectedOrgMultipleList', { static: false }) subscribeOrg: MatSelectionList;
    @ViewChild('selectedOrgAssetMultipleList', { static: false }) subscribeAsset: MatSelectionList;
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatTable, { static: false }) matTable: MatTable<any>;

    @Input() data: BehaviorSubject<TableData[]>;
    @Input() allTableColumns: BehaviorSubject<TableColumn[]>;
    @Input() selectedColumns: string[] = [];
    @Input() showRowExpansion: boolean;
    @Input() noRecordMsg: string;
    @Input() wordWrap = false;
    @Input() pagination: BehaviorSubject<PaginationInterface>;
    @Input() hidePaginator;
    @Input() defaultPaginator;
    @Input() hideTableColumns;
    @Input() columnSearchRequired = false;
    @Input() recordActions: TableRecordAction[];
    @Input() noLog = false;
    @Input() customActionHeaderName: string;
    @Input() columnselectSearchRequired = false;
    @Input() disablePrevious = false;
    @Input() disableNext = false;
    @Input() newCustomPaginator = false;
    @Input() customPageIndex;
    @Input() pageIndexSelected: number;
    @Input() customSize = [10, 20, 50, 100];
    @Input() customSizeOptions = [];
    @Input() sizeSelected = 10;
    @Input() loading: boolean;
    @Input() cellDataTypes: TableCellDataTypesInterface;
    @Input() passwordColumns?: any[]; // This is to identify the columns of type password
    @Input() enableColumnEdit: boolean;
    @Input() public columnsToUpdate: { [property: string]: boolean } = {};
    @Input() fieldsDisabledForEdit: string[] = [];
    @Input() recordFieldsDisabledForEdit: { [property: string]: string[] } = {};
    @Input() noSticky: boolean;
    @Input() showCount: boolean;
    @Input() clearSearchedFilter = false;
    @Input() searchInputTemplate: TemplateRef<any>;
    @Input() expandedElementTemplate: TemplateRef<any>;
    @Input() aggregationsTemplate: TemplateRef<any>;
    @Input() alwaysShowPaginator = false;
    @Input() showRowBorders = false;
    @Input() templates: { [key: string]: TemplateRef<any> } = {};
    @Input() enableColumnsConfig = false;
    @Input() columnConfigKey = '';
    @Input() columnConfigType = '';

    @Output() fetchItemsInPage = new EventEmitter();
    @Output() refreshItemsWithNewPageSize = new EventEmitter();
    @Output() fetchItemsInPageWithPageIndex = new EventEmitter();
    @Output() onSelectCheckbox = new EventEmitter();
    @Output() onSingleClickEvent = new EventEmitter();
    @Output() onDoubleClickEvent = new EventEmitter();
    @Output() recordSelectedEvent = new EventEmitter();
    @Output() filesChanged = new EventEmitter();
    @Output() DownloadSourceTemplate = new EventEmitter();
    @Output() downloadFile = new EventEmitter();
    @Output() emitTableValues = new EventEmitter();
    @Output() editColumnEvent = new EventEmitter();
    @Output() onMenuSelect = new EventEmitter();
    @Output() onExpandRow = new EventEmitter();
    @Output() clearFilter = new EventEmitter();

    selectedAsset = new UntypedFormControl();
    searchColumnControl = new UntypedFormControl();
    allColumns: TableColumn[] = [];
    clonedColumns: string[];
    dataSource = new MatTableDataSource([]);
    selection = new SelectionModel(true, []);

    expandedElement;
    selectAll;
    searchColumns: string;
    actionColumns = ['table-actions', 'log', 'checkbox'];
    throwAtLast = ['table-actions', 'log'];
    showLog = true;
    noRecordsFound = false;
    disbaledData = false;
    searchColumnValue = '';
    selectedStatus = '';
    searchOrgString = '';
    searchAssetOrgString = '';
    selectedSearchColumn = '';
    enteredSearchValue = '';
    selectedOrgsArray = [];
    selectedAssetsArray = [];
    newOrgAdded: Subscription;
    unSubscribe = new Subject<void>();
    columnKeyPair: { [property: string]: TableColumn };
    specialColumnKeyPair: { [property: string]: TableColumn };
    selectAllRecords = false;
    attachmentData = {
        filename: '',
        url: '',
        title: translate('Add File'),
        return: 'true',
    };
    clearFiles = new BehaviorSubject(false);
    actionVisibility: { [property: string]: { [property: string]: boolean } } = {};
    lastPageDisabled: boolean;
    columnTypePair: { [property: string]: string } = {};
    show = false;
    selectedRecordsCount: number;
    columnConfig: { [columnId: string]: { order: number; width?: number } } = {};
    columnWidths: { [columnId: string]: number } = {};
    private readonly skipResizeColumns = ['table-actions', 'log', 'checkbox', 'children'];

    get hasMultiLineHeader() {
        return (
            !!this.searchInputTemplate ||
            !!this.aggregationsTemplate ||
            this.enableColumnEdit ||
            (this.columnSearchRequired && !this.clearSearchedFilter)
        );
    }

    constructor(
        private _broadcaster: BroadcasterService,
        private _libUtils: UtilsService,
        private _commonUtils: CommonUtilsService,
        public sanitizer: DomSanitizer,
        private renderer: Renderer2,
        private el: ElementRef,
        private store$: Store
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.columnConfigKey?.currentValue && this.enableColumnsConfig) {
            this.columnConfig = this.loadColumnConfigFromStorage() || {};
        }

        if (changes.selectedColumns?.currentValue?.length > 0) {
            const hasConfig = this.enableColumnsConfig && this.columnConfig && Object.keys(this.columnConfig).length > 0;
            if (hasConfig) {
                this.updateSelectedColumns();
            } else {
                this.clonedColumns = CommonUtilsService.cloneObject(changes.selectedColumns.currentValue);
            }
            this.pushSpecialColumns();
            this.generateColumnTypes();
        }

        if (this.columnConfig && this.enableColumnsConfig) {
            this.initColumnWidths();
        }
    }

    buildSelectionModal = () => {
        this.selection.clear();
        for (let i = 0; i < this.dataSource.data.length; i++) {
            if (this.dataSource.data[i].selected) {
                this.selection.select(this.dataSource.data[i]);
            } else {
                this.selection.deselect(this.dataSource.data[i]);
            }
        }
        this.onSelectCheckbox.emit(this.selection.selected);
    };

    checkboxSelect = (checked: boolean, column: TableColumn) => {
        if (checked) {
            if (!this.clonedColumns.includes(column.id)) {
                this.clonedColumns.push(column.id);
            }
        } else {
            const index = this.clonedColumns.indexOf(column.id);
            if (index !== -1) {
                this.clonedColumns.splice(index, 1);
            }
        }

        if (this.enableColumnsConfig) {
            const allColumnIds = this.allColumns.map(c => c.id);
            this.clonedColumns.sort((a, b) => allColumnIds.indexOf(a) - allColumnIds.indexOf(b));
        }

        if (this.actionColumns.length !== 0) {
            this.actionColumns.forEach(eachAction => {
                const actionIndex = this.clonedColumns.indexOf(eachAction);
                if (actionIndex !== -1) {
                    this.clonedColumns.splice(actionIndex, 1);
                }
            });
        }
        this.checkFiltersSelected();
    };

    cloneSubmit = () => {
        const actionColumns = this.actionColumns?.filter(column => this.throwAtLast.indexOf(column) === -1);
        const newColumns = this.clonedColumns?.filter(column => {
            if (actionColumns.indexOf(column) === -1 || column === 'checkbox') {
                return column;
            }
        });
        const tempColumns = newColumns?.filter(column => this.throwAtLast.indexOf(column) === -1);
        if (tempColumns && tempColumns.length > 0) {
            // update condition for row record checkbox
            if (tempColumns.length === 1 && tempColumns[0] === 'checkbox') {
                this._libUtils.alertError('Select atleast one column name');
            } else {
                this.selectedColumns = [];
                tempColumns && tempColumns.forEach(column => this.selectedColumns.push(column));
                this.pushSpecialColumns();
                this.generateColumnTypes();
                this.enableColumnsConfig && this.updateColumnOrder();
            }
        } else if (!this.selectedColumns?.length) {
            this._libUtils.alertError('Select atleast one column name');
        }
        if (this.selectAll) {
            this._commonUtils.setInStorage('filterColumnLog', true);
        } else {
            this._commonUtils.setInStorage('filterColumnLog', false);
        }
    };

    pushSpecialColumns = () => {
        this.throwAtLast
            .filter(columnName => this.selectedColumns.indexOf(columnName) === -1)
            .forEach(columnName => {
                if (columnName === 'table-actions') {
                    if (this.recordActions && this.recordActions.length > 0) {
                        this.selectedColumns.push(columnName);
                    }
                } else if (columnName === 'log') {
                    !this.noLog && this.selectedColumns.push(columnName);
                } else {
                    this.selectedColumns.push(columnName);
                }
            });
        this.allColumns
            .filter(column => column.hideInAllColumnsList && !column.hide)
            .filter(column => this.selectedColumns.indexOf(column.id) === -1)
            .forEach(column => this.selectedColumns.unshift(column.id));
    };

    selectAllFilters = selectedAll => {
        const oldColumns = this.clonedColumns?.slice(0);
        const actionColumns = this.actionColumns.filter(column => this.throwAtLast.indexOf(column) === -1);
        this.clonedColumns = oldColumns?.filter(column => actionColumns.indexOf(column) > -1);
        if (selectedAll) {
            this.allColumns
                .filter(column => !column.hideInAllColumnsList)
                .filter(
                    column =>
                        this.actionColumns.indexOf(column.id) === -1 &&
                        !(this.hideTableColumns && this.hideTableColumns.indexOf(column.id) > -1)
                )
                .forEach(column => {
                    this.clonedColumns.push(column.id);
                });
        }
    };

    onSingleClick = record => {
        this.onSingleClickEvent.emit(record);
    };

    onDoubleClick = record => {
        // this.doubleClicked = true;
        const recordClone = CommonUtilsService.cloneObject(record);
        // recordClone['drawer'] = this.drawer;
        this.onDoubleClickEvent.emit(recordClone);
    };

    isLastPageDisabled = () => {
        this.lastPageDisabled = true;
        if (this.hidePaginator === true) {
            this.lastPageDisabled = true;
            return;
        }
        let disabled = true;
        if (this.pagination && this.pagination.value.page) {
            const keys = Object.keys(this.pagination.value.page);
            const lastValue = this.pagination.value.page[keys[keys.length - 1]];
            disabled = lastValue && lastValue !== undefined && lastValue.length > 0;
        }
        this.lastPageDisabled = disabled;
    }; /*EI-1282*/

    checkColumnsCount = () => {
        if (this.allColumns && this.actionColumns) {
            const filteredColumns = this.allColumns && this.allColumns.filter(column => this.actionColumns.indexOf(column.id) === -1);
            return filteredColumns && filteredColumns.length >= 5;
        }
        return true;
    };

    // checkRecord = (element) => {
    //     this.checkMasterRecord.emit(element);
    // }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? // this.selection.selected.forEach(element => {
              //     if (element && element['Role Name'] && (element['Role Name'] === 'CUSTOMER_ADMIN' || element['Role Name'] === 'PROVIDER_ADMIN')) {
              //     } else {
              //         this.selection.deselect(element);
              //     }
              // }) :
              this.selection.clear()
            : this.dataSource.data.forEach(row => this.selection.select(row));
        this.onSelectCheckbox.emit(this.selection.selected);
    }

    checkFiltersSelected = () => {
        const leftOutColumns =
            this.allColumns &&
            this.allColumns
                .filter(column => !column.hideInAllColumnsList)
                .filter(column => !this.hideTableColumns || (this.hideTableColumns && this.hideTableColumns.indexOf(column.id) === -1))
                .filter(column => this.clonedColumns && this.clonedColumns.indexOf(column.id) === -1);
        this.selectAll = leftOutColumns && leftOutColumns.length === 0;
    };

    getColumn = (columnId: string): TableColumn => {
        const column =
            this.allColumns.find(tableCoulmn => tableCoulmn.id === columnId) ||
            (this.allTableColumns?.value?.find((col: TableColumn) => col.id === columnId) as TableColumn);
        if (this.passwordColumns?.indexOf(column?.name) >= 0) {
            column.type = 'PASSWORD';
        }
        if (column) {
            return column;
        }
        const throwAtLastColumn = this.throwAtLast.indexOf(columnId) > -1;
        if (throwAtLastColumn) {
            if (columnId === 'log') {
                return {
                    id: columnId,
                    hide: false,
                    hideInAllColumnsList: true,
                    icon: undefined,
                    name: translate('Log'),
                    type: 'ALL_COLUMNS_LIST',
                    options: undefined,
                };
            } else if (columnId === 'table-actions') {
                return {
                    id: columnId,
                    hide: false,
                    hideInAllColumnsList: true,
                    icon: undefined,
                    name: translate('Actions'),
                    type: 'ACTIONS',
                    options: undefined,
                };
            }
        }
        return;
    };

    dropColumnMenu(event: CdkDragDrop<any[]>) {
        if (this.searchColumns) return;
        moveItemInArray(this.allColumns, event.previousIndex, event.currentIndex);
        if (this.enableColumnsConfig) {
            const allColumnIds = this.allColumns.map(c => c.id);
            this.clonedColumns.sort((a, b) => allColumnIds.indexOf(a) - allColumnIds.indexOf(b));
        }
    }

    getSpecialColumns = (columnId: string): TableColumn => {
        let column: TableColumn;
        switch (columnId) {
            case 'log':
                column = {
                    id: columnId,
                    hide: false,
                    hideInAllColumnsList: true,
                    icon: undefined,
                    name: translate('Log'),
                    type: 'ALL_COLUMNS_LIST',
                    options: undefined,
                };
                break;
            case 'table-actions':
                column = {
                    id: columnId,
                    hide: false,
                    hideInAllColumnsList: true,
                    icon: undefined,
                    name: translate('Actions'),
                    type: 'ACTIONS',
                    options: undefined,
                };
                break;
        }
        return column;
    };

    generateColumnTypes = () => {
        this.columnTypePair = {};
        this.selectedColumns.forEach(columnId => {
            this.columnTypePair[columnId] = this.getColumnType(columnId);
        });
        // tslint:disable-next-line: no-unused-expression
        this.show = Object.keys(this.columnTypePair).indexOf('undefined') === -1 && Object.keys(this.columnTypePair).length > 0;
        if (this.show) {
            setTimeout(() => this.matTable?.updateStickyColumnStyles(), 200);
        }
        if (this.show && this.enableColumnsConfig) {
            setTimeout(() => this.addResizeHandles(), 0);
        }
    };

    getColumnType = (columnId: string): string => {
        const column = this.getColumn(columnId);
        return column && column.type;
    };

    generateSpecialColumnsList = () => {
        this.specialColumnKeyPair = {};
    };

    toggleRecordsSelection = (isSelected: boolean) => {
        this.dataSource.data.forEach(record => {
            record.selected = isSelected;
            this.recordSelectedEvent.emit(record);
        });
        this.setSelectedRecordsCount();
    };

    onRecordSelectionChange = (changedRecord: TableData) => {
        this.checkAllRecordsSelected();
        this.recordSelectedEvent.emit(changedRecord);
    };

    checkAllRecordsSelected = () => {
        const nonSelectedRecords = this.getNonSelectedRecords();
        this.selectAllRecords = nonSelectedRecords.length === 0 && this.dataSource.data.length > 0;
        this.setSelectedRecordsCount();
    };

    setSelectedRecordsCount = () => (this.selectedRecordsCount = this.getSelectedRecords().length);

    getSelectedRecords = (): any[] => this.dataSource.data.filter(record => record.selected);

    getNonSelectedRecords = (): any[] => this.dataSource.data.filter(record => !record.selected);

    fileChanged = (files, record) => {
        record.files = [];
        files.forEach((file: File) => {
            record.files.push(file);
        });
        this.filesChanged.emit(record);
    };

    changedFileName = (fileName: string, record) => {
        record.fileName = fileName;
        this.filesChanged.emit(record);
    };

    downloadSourceTemplateUrl = element => {
        this.DownloadSourceTemplate.emit(element);
    };

    checkForDynamicActions = (record, action) => {
        if (record?.dynamicActions?.length > 0) {
            const dynamicAction = record.dynamicActions;
            for (let i = 0; i < dynamicAction.length; i++) {
                if (dynamicAction[i].displayName === action.displayName) {
                    if (dynamicAction[i].erroneous === false) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    downloadUplodedFile = (record, fileName) => {
        record.downloadFile = fileName;
        this.downloadFile.emit(record);
    };

    copyToInboundRequestId = record => {
        const input = document.createElement('input');
        input.setAttribute('value', record.requestId);
        document.body.appendChild(input);
        input.select();
        const result = document.execCommand('copy');
        document.body.removeChild(input);
        return result;
    };

    buildClonedColumns = () => {
        this.clonedColumns = this.selectedColumns
            .filter(columnId => {
                const column = this.allColumns.find(columnRecord => columnRecord.id === columnId);
                return column && !column.hideInAllColumnsList;
            })
            .slice(0);
    };

    buildActionsShowHideModal = (records: TableData[]) => {
        this.actionVisibility = {};
        (records || []).forEach(record => {
            this.actionVisibility[record.recordId] = {};
            (this.recordActions || []).forEach(action => {
                this.actionVisibility[record.recordId][action.displayName] = this.checkForDynamicActions(record, action);
            });
        });
    };

    trackByActionsMethod = (_index: number, action: any): number => {
        return action.displayName;
    };

    trackBySelectedColumnsMethod = (_index: number, columnId: any): number => {
        return columnId;
    };

    trackByAllColumnsMethod = (_index: number, columnId: any): number => {
        return columnId;
    };

    /**
     * Track table rows by record ID for better performance
     * @param index Row index
     * @param element Table data element
     * @returns Record ID for tracking
     */
    trackByRecordId = (index: number, element: any): string => {
        return element?.recordId || index;
    };

    clickedCell = (data: TableColumn, event: Event, value: TableData) => {
        if (data.callback) {
            event.stopPropagation();
            data.callback(value);
        }
    };

    decodeTextFieldValue = (value: any, returnString?: boolean) => {
        return CommonUtilsService.decodeTextFieldValue(value, this.sanitizer, returnString);
    };

    onOptionSelect = (key: string, value: string, id: string) => {
        this.onMenuSelect.emit({ key, value, id });
    };

    /* Column Config Methods */

    private loadColumnConfigFromStorage = (): { [columnId: string]: { order: number; width?: number } } | null => {
        if (!this.columnConfigKey) return null;
        let taskManagement: any = null;
        this.store$
            .select(getTaskManagementPreference$)
            .subscribe(tm => {
                taskManagement = tm || {};
            })
            .unsubscribe();
        let config = null;
        switch (this.columnConfigType) {
            case 'TASK_DEFINITION':
                config = taskManagement.definitions?.[this.columnConfigKey]?.columnConfig || null;
                break;
            case 'TASK_INSTANCE':
                config = taskManagement.instances?.[this.columnConfigKey]?.columnConfig || null;
                break;
        }
        return config;
    };

    private saveColumnConfigToStorage = (config: { [columnId: string]: { order: number; width?: number } }) => {
        if (!this.columnConfigKey) return;
        let currentPrefs: any = null;
        this.store$
            .select(getUiPreferences$)
            .subscribe(prefs => {
                currentPrefs = prefs ?? {};
            })
            .unsubscribe();

        const taskManagement = CommonUtilsService.cloneObject(currentPrefs.taskManagement || {});
        switch (this.columnConfigType) {
            case 'TASK_DEFINITION':
                taskManagement.definitions = taskManagement.definitions || {};
                taskManagement.definitions[this.columnConfigKey] = taskManagement.definitions[this.columnConfigKey] || {};
                taskManagement.definitions[this.columnConfigKey].columnConfig = config;
                break;
            case 'TASK_INSTANCE':
                taskManagement.instances = taskManagement.instances || {};
                taskManagement.instances[this.columnConfigKey] = taskManagement.instances[this.columnConfigKey] || {};
                taskManagement.instances[this.columnConfigKey].columnConfig = config;
                break;
        }

        const updatedPreferences = {
            ...currentPrefs,
            taskManagement,
        };

        this.store$.dispatch(
            SaveUiPreferences({ preferences: updatedPreferences, successMessage: 'Column configuration saved successfully' })
        );
        this.columnConfig = config;
        this._broadcaster.broadcast('columnConfigChanged', { key: this.columnConfigKey, type: this.columnConfigType, config });
    };

    private updateSelectedColumns = () => {
        const tempColumns = [];
        if (this.columnConfig && Object.keys(this.columnConfig).length > 0) {
            Object.keys(this.columnConfig)
                .sort((a, b) => (this.columnConfig[a]?.order || 0) - (this.columnConfig[b]?.order || 0))
                .forEach(columnId => {
                    tempColumns.push(columnId);
                });
        }
        this.selectedColumns = CommonUtilsService.cloneObject(tempColumns);
        this.clonedColumns = CommonUtilsService.cloneObject(this.selectedColumns);
    };

    private setInitialColumnConfig = () => {
        if (this.columnConfigKey) {
            this.columnConfig = this.loadColumnConfigFromStorage() || {};
        }
        const hasConfig = this.columnConfig && Object.keys(this.columnConfig).length > 0;
        if (this.selectedColumns.length > 0 && !hasConfig) {
            const config: { [columnId: string]: { order: number } } = {};
            this.selectedColumns
                .filter(colId => colId !== 'children' && colId !== 'checkbox' && colId !== 'table-actions' && colId !== 'log')
                .forEach((colId, index) => {
                    config[colId] = { order: index };
                });
            if (this.columnConfigKey) {
                this.saveColumnConfigToStorage(config);
            }
        } else if (hasConfig) {
            const tempColumns: string[] = [];
            Object.keys(this.columnConfig)
                .sort((a, b) => (this.columnConfig[a]?.order || 0) - (this.columnConfig[b]?.order || 0))
                .forEach(columnId => {
                    tempColumns.push(columnId);
                });
            this.selectedColumns = CommonUtilsService.cloneObject(tempColumns);
        }
    };

    private updateColumnOrder = () => {
        const config = {};
        this.selectedColumns
            .filter(colId => colId !== 'children' && colId !== 'checkbox' && colId !== 'table-actions' && colId !== 'log')
            .forEach((colId, index) => {
                if (this.columnConfig[colId]) {
                    config[colId] = { ...this.columnConfig[colId], order: index };
                } else {
                    config[colId] = { order: index };
                }
            });
        if (this.columnConfigKey) {
            this.saveColumnConfigToStorage({ ...config });
        }
    };

    private initColumnWidths = () => {
        if (this.columnConfig) {
            Object.keys(this.columnConfig).forEach(colId => {
                const w = this.columnConfig[colId]?.width;
                if (w) {
                    this.columnWidths[colId] = w;
                }
            });
        }
    };

    private addResizeHandles = () => {
        this.selectedColumns
            .filter(colId => !this.skipResizeColumns.includes(colId))
            .forEach(columnId => {
                const th: HTMLElement = this.el.nativeElement.querySelector(`th.mat-column-${columnId}`);
                if (!th || th.querySelector('.resize-handle')) return;

                const savedWidth = this.columnWidths[columnId];
                if (savedWidth) {
                    this.renderer.setStyle(th, 'min-width', `${savedWidth}px`);
                    this.renderer.setStyle(th, 'width', `${savedWidth}px`);
                }

                const handle = this.renderer.createElement('div');
                this.renderer.addClass(handle, 'resize-handle');
                this.renderer.listen(handle, 'mousedown', (event: MouseEvent) => {
                    this.startResize(event, columnId, th);
                });
                this.renderer.appendChild(th, handle);
            });
    };

    private startResize = (event: MouseEvent, columnId: string, thEl: HTMLElement) => {
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = this.columnWidths[columnId] || thEl.offsetWidth;

        const onMouseMove = (e: MouseEvent) => {
            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);
            this.columnWidths[columnId] = newWidth;
            this.renderer.setStyle(thEl, 'min-width', `${newWidth}px`);
            this.renderer.setStyle(thEl, 'width', `${newWidth}px`);
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            this.emitColumnWidths();
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    private emitColumnWidths = () => {
        const config: { [columnId: string]: { order: number; width?: number } } = {
            ...this.columnConfig,
        };
        Object.keys(this.columnWidths).forEach(colId => {
            config[colId] = {
                ...(config[colId] || { order: 0 }),
                width: this.columnWidths[colId],
            };
        });
        if (this.columnConfigKey) {
            this.saveColumnConfigToStorage(config);
        }
    };

    public ngOnInit() {
        this.dataSource.sort = this.sort;

        this.store$
            .select(getTaskManagementPreference$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(taskManagement => {
                if (this.enableColumnsConfig && this.columnConfigKey && taskManagement) {
                    const newConfig = this.loadColumnConfigFromStorage();
                    if (newConfig) {
                        this.columnConfig = newConfig;
                        this.updateSelectedColumns();
                        this.pushSpecialColumns();
                        this.generateColumnTypes();
                        this.initColumnWidths();
                    }
                }
            });

        this.allTableColumns
            .pipe(
                takeUntil(this.unSubscribe),
                delay(0),
                distinctUntilChanged((prev, current) => JSON.stringify(prev) === JSON.stringify(current))
            )
            .subscribe(async columns => {
                if (!columns || columns.length === 0) {
                    return;
                }
                this.show = false;
                this.allColumns = columns;
                this.buildClonedColumns();
                await this._libUtils.waitForCertainTime(100);
                if (this.enableColumnsConfig) {
                    this.setInitialColumnConfig();
                    this.updateSelectedColumns();
                }
                this.pushSpecialColumns();
                this.columnKeyPair = {};
                this.allColumns.forEach(column => {
                    this.columnKeyPair[column.id] = column;
                });
                this.checkFiltersSelected();
                this.generateColumnTypes();
            });

        this.data
            .pipe(
                takeUntil(this.unSubscribe),
                delay(10),
                distinctUntilChanged((prev, current) => JSON.stringify(prev) === JSON.stringify(current))
            )
            .subscribe(data => {
                this.buildActionsShowHideModal(data);
                this.buildSelectionModal();
                this.dataSource.data = data || [];
                this.checkAllRecordsSelected();
                this.dataSource.filterPredicate = (tableData, filter) => {
                    return this.selectedColumns.some(ele => {
                        return (
                            ele !== 'table-actions' &&
                            ele !== 'log' &&
                            tableData[ele] &&
                            tableData[ele].toLocaleLowerCase().indexOf(filter) !== -1
                        );
                    });
                };
            });
        if (this.pagination) {
            this.pagination.pipe(takeUntil(this.unSubscribe)).subscribe(this.isLastPageDisabled);
        }
        !this.noLog &&
            this.selectedColumns &&
            this.selectedColumns.indexOf('log') === -1 &&
            this.checkColumnsCount() &&
            this.selectedColumns.push('log');
        this._broadcaster
            .on('selectFilterLogColumn')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(async () => {
                await this._libUtils.waitForCertainTime(100);
                this.selectAll = true;
                this.selectAllFilters(true);
                this.cloneSubmit();
            });
        this._broadcaster
            .on('columnConfigChanged')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(async (event: any) => {
                if (this.columnConfigKey && event.key === this.columnConfigKey && event.type === this.columnConfigType) {
                    this.columnConfig = event.config;
                    this.updateSelectedColumns();
                    this.pushSpecialColumns();
                    this.generateColumnTypes();
                    this.initColumnWidths();
                }
            });
    }

    public ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.paginator?._intl && (this.paginator._intl.itemsPerPageLabel = translate('Items per page'));
    }

    public ngOnDestroy() {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
